@if (feed) {
  <section class="sftp-access">
    <div class="heading"><div><h3>SFTP access — {{feed.name}}</h3><p>Give this partner its own login and upload directory.</p></div>
    <button mat-stroked-button type="button" (click)="refresh()" [disabled]="busy || loading || disabled">Refresh access</button>
  </div>
  @if (loading) {
    <p role="status">Verifying SFTP access…</p>
  }
  @if (error) {
    <p class="error" role="alert">{{error}}</p>
  }
  @if (notice) {
    <p role="status">{{notice}}</p>
  }
  @if (access; as state) {
    <p><strong>Status:</strong> {{state.status.replace('_', ' ')}}</p>
    @if (!state.configured) {
      <p>SFTP account management has not been enabled for this environment.</p>
    }
    @if (state.message) {
      <p class="error">{{state.message}}</p>
    }
    @if (state.account?.lastError) {
      <p class="error">{{state.account?.lastError}}</p>
    }
    @if (state.status === 'mismatch') {
      <p>AWS access and the saved feed do not agree. An administrator must review the mismatch before changes are allowed.</p>
    }
    @if (state.account) {
      <div class="connection">
        <span><strong>Host:</strong> {{state.host}}</span><span><strong>Port:</strong> {{state.port}}</span>
        <span><strong>Username:</strong> {{state.account.username}}</span><span><strong>Upload directory:</strong> {{state.uploadPath}}</span>
        <span><strong>Storage directory:</strong> {{state.account.folder}}</span>
        <span><strong>Authentication:</strong> {{state.authMethod === 'password' ? 'Password' : state.authMethod === 'ssh_key' ? 'SSH key' : 'Unverified'}}</span>
      </div>
    }
    @if (state.status === 'ready') {
      <button mat-button type="button" (click)="copyConnection()">Copy connection details</button>
    }
    @if (state.account?.pendingAction) {
      <button class="reconcile-action" mat-raised-button type="button" color="primary" [disabled]="busy || loading || disabled" (click)="submit(true)">Reconcile pending change</button>
    }
    @if (state.account?.pendingAction) {
      <p>A saved change is incomplete. Reconciliation checks the saved operation and retries it without creating another account.</p>
    }
    @if (feed.transportConfig?.sftp?.enabled !== true || feed.status === 'archived') {
      <p>Enable SFTP on a non-archived feed before managing access.</p>
    }
    @if (canChange) {
      <form (ngSubmit)="submit()">
        <div class="sftp-actions wide" role="group" aria-label="SFTP actions">
          @if (!state.account) {
            <button mat-stroked-button type="button" [attr.aria-pressed]="action === 'create'" (click)="chooseAction('create')">Create account</button>
            <button mat-stroked-button type="button" [attr.aria-pressed]="action === 'link'" (click)="chooseAction('link')">Link reviewed account</button>
          }
          @if (state.account) {
            <button mat-stroked-button type="button" [attr.aria-pressed]="action === 'set_auth'" (click)="chooseAction('set_auth')">{{state.authMethod === 'password' ? 'Reset password / change authentication' : 'Change authentication'}}</button>
            <button mat-stroked-button type="button" [attr.aria-pressed]="action === 'directory'" (click)="chooseAction('directory')">Change directory</button>
            @if (state.authMethod === 'ssh_key') {
              <button mat-stroked-button type="button" [attr.aria-pressed]="action === 'add_key'" (click)="chooseAction('add_key')">Add SSH key</button>
              <button mat-stroked-button type="button" [attr.aria-pressed]="action === 'revoke_key'" (click)="chooseAction('revoke_key')">Revoke SSH key</button>
            }
          }
        </div>
        @if (action === 'create' || action === 'link') {
          <mat-form-field appearance="outline"><mat-label>Partner username</mat-label><input matInput name="username" [(ngModel)]="username" autocomplete="off" maxlength="100" required></mat-form-field>
        }
        @if (action === 'link') {
          <p>The existing account must have been reviewed and imported for this feed and exact directory. Linking does not adopt unrelated AWS accounts.</p>
        }
        @if (action === 'create' || action === 'set_auth') {
          <mat-form-field appearance="outline"><mat-label>Authentication method</mat-label><mat-select name="authMethod" [(ngModel)]="authMethod" (selectionChange)="clearCredentials()"><mat-option value="ssh_key">SSH key</mat-option><mat-option value="password">Username and password</mat-option></mat-select></mat-form-field>
          @if (authMethod === 'password') {
            <mat-form-field appearance="outline">
              <mat-label>New password</mat-label>
              <input matInput [type]="passwordVisible ? 'text' : 'password'" name="secretValue" [(ngModel)]="secretValue" autocomplete="new-password" minlength="16" maxlength="128" required>
              <button mat-icon-button matSuffix type="button" (click)="passwordVisible = !passwordVisible" [attr.aria-label]="passwordVisible ? 'Hide password' : 'Show password'" [matTooltip]="passwordVisible ? 'Hide password' : 'Show password'">
                <mat-icon>{{passwordVisible ? 'visibility_off' : 'visibility'}}</mat-icon>
              </button>
              <mat-hint>16–128 characters. Share securely with the partner; saved passwords cannot be retrieved.</mat-hint>
            </mat-form-field>
          }
          @if (action === 'set_auth') {
            <p>This replaces the account’s authentication settings. For SSH key rotation, use Add SSH key first.</p>
          }
        }
        @if (action === 'add_key' || ((action === 'create' || action === 'set_auth') && authMethod === 'ssh_key')) {
          <mat-form-field class="wide" appearance="outline"><mat-label>SSH public key</mat-label><textarea matInput name="publicKey" [(ngModel)]="publicKey" rows="3" maxlength="2048" required></textarea><mat-hint>The partner keeps the private key. Paste only their OpenSSH public key.</mat-hint></mat-form-field>
        }
        @if (action === 'directory') {
          <mat-form-field class="wide" appearance="outline"><mat-label>Storage directory</mat-label><input matInput name="folder" [(ngModel)]="folder" required><mat-hint>Use this environment’s customer-feeds directory. Existing files are not moved.</mat-hint></mat-form-field>
        }
        @if (action === 'directory') {
          <mat-checkbox class="wide" name="sessionsDrained" [(ngModel)]="sessionsDrained">The partner has disconnected active SFTP sessions. Existing files will remain in the previous directory.</mat-checkbox>
        }
        @if (action === 'revoke_key') {
          <mat-form-field appearance="outline"><mat-label>Key to revoke</mat-label><mat-select name="revokeKeyId" [(ngModel)]="revokeKeyId" (selectionChange)="replacementVerified = false">@for (key of state.keys; track key) {
          <mat-option [value]="key.id">{{key.fingerprint}}</mat-option>
        }</mat-select></mat-form-field>
        <mat-form-field appearance="outline"><mat-label>Verified replacement key</mat-label><mat-select name="replacementKeyId" [(ngModel)]="replacementKeyId" (selectionChange)="replacementVerified = false">@for (key of state.keys; track key) {
        <mat-option [value]="key.id" [disabled]="key.id === revokeKeyId">{{key.fingerprint}}</mat-option>
      }</mat-select></mat-form-field>
      <mat-checkbox name="replacementVerified" [(ngModel)]="replacementVerified">The partner successfully logged in using the replacement key.</mat-checkbox>
    }
    <div class="wide">
      <button mat-raised-button color="primary" type="submit" [disabled]="!canSubmit">{{submitLabel}}</button>
    </div>
  </form>
}
@if (busy) {
  <p role="status">Applying and verifying the SFTP change…</p>
}
@if (state.keys?.length) {
  <details><summary>Registered SSH keys</summary>@for (key of state.keys; track key) {
  <p class="fingerprint">{{key.fingerprint}}</p>
}</details>
}
@if (state.audit?.length) {
  <details>
    <summary>Recent access changes</summary>
    <ul>
      @for (event of state.audit; track event) {
        <li>
          {{event.createdAt | date:'medium'}} — {{event.action}} — {{event.outcome}} — administrator
          {{event.actorFirstName}} {{event.actorLastName}}@if (event.actorEmail) {
          <span> ({{event.actorEmail}})</span>
        }
      </li>
    }
  </ul>
</details>
}
}
</section>
}
